const PERSISTED_STATE_KEY = 'vuex'
const PERSISTED_STATE_CREATED_AT_KEY = 'vuex_created_at'
const DEFAULT_SESSION_TTL_MS = 24 * 60 * 60 * 1000
const DEFAULT_AUTH_STORAGE_KEYS = [
	'auth.token_local',
	'auth._token.local',
	'auth._token_expiration.local',
	'auth.strategy'
]

type PersistedSessionOptions = {
	authStorageKeys?: string[]
}

type CookieStorage = {
	remove?: (key: string, options?: { path?: string }) => void
}

export const getPersistedSessionStorage = (): Storage | null => {
	if (typeof window === 'undefined') {
		return null
	}

	try {
		return window.localStorage
	} catch (error) {
		return null
	}
}

const getSessionTtlMs = (sessionTtlMs?: number | string): number => {
	const parsedSessionTtlMs = Number(sessionTtlMs)
	return Number.isFinite(parsedSessionTtlMs) && parsedSessionTtlMs > 0
		? parsedSessionTtlMs
		: DEFAULT_SESSION_TTL_MS
}

const getAuthStorageKeys = (authStorageKeys?: string[]) => (
	authStorageKeys && authStorageKeys.length > 0 ? authStorageKeys : DEFAULT_AUTH_STORAGE_KEYS
)

export const clearPersistedStateCache = () => {
	const localStorage = getPersistedSessionStorage()

	if (localStorage) {
		localStorage.removeItem(PERSISTED_STATE_KEY)
		localStorage.removeItem(PERSISTED_STATE_CREATED_AT_KEY)
	}
}

export const clearPersistedSession = (cookies?: CookieStorage, options: PersistedSessionOptions = {}) => {
	const localStorage = getPersistedSessionStorage()
	const authStorageKeys = getAuthStorageKeys(options.authStorageKeys)

	clearPersistedStateCache()

	if (localStorage) {
		authStorageKeys.forEach(key => localStorage.removeItem(key))
	}

	if (cookies && cookies.remove) {
		authStorageKeys.forEach(key => cookies.remove(key, {path: '/'}))
	}
}

export const markPersistedSessionStarted = () => {
	const localStorage = getPersistedSessionStorage()

	if (localStorage) {
		localStorage.setItem(PERSISTED_STATE_CREATED_AT_KEY, Date.now().toString())
	}
}

export const ensurePersistedSessionStarted = () => {
	const localStorage = getPersistedSessionStorage()

	if (localStorage && !localStorage.getItem(PERSISTED_STATE_CREATED_AT_KEY)) {
		markPersistedSessionStarted()
	}
}

export const clearExpiredPersistedSession = (
	cookies?: CookieStorage,
	sessionTtlMs?: number | string,
	options: PersistedSessionOptions = {}
): boolean => {
	const localStorage = getPersistedSessionStorage()

	if (!localStorage) {
		return false
	}

	const createdAt = Number(localStorage.getItem(PERSISTED_STATE_CREATED_AT_KEY))
	const hasPersistedState = localStorage.getItem(PERSISTED_STATE_KEY) !== null

	if (!hasPersistedState) {
		localStorage.removeItem(PERSISTED_STATE_CREATED_AT_KEY)
		return false
	}

	if (!createdAt || createdAt > Date.now()) {
		clearPersistedSession(cookies, options)
		return true
	}

	if (Date.now() - createdAt <= getSessionTtlMs(sessionTtlMs)) {
		return false
	}

	clearPersistedSession(cookies, options)
	return true
}
